'use client';

import { motion } from "framer-motion";
import { 
  ShoppingCart, 
  Leaf, 
  Building2, 
  FileText, 
  BadgeDollarSign, 
  Stethoscope, 
  Scale, 
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
  Car,
  Zap,
  Globe,
  Utensils
} from "lucide-react";
import Link from "next/link";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useParams } from "next/navigation";
import { localizeSpelling } from "@/lib/spelling-localization";

const industriesData = [
  {
    name: "AutoTech",
    fullName: "Automotive Technology",
    icon: Car,
    gradient: "from-red-500 via-orange-400 to-amber-500",
    description: "Advanced AI and IoT solutions for automotive diagnostics, safety systems, and autonomous driving technologies.",
    solutions: [
      "Vehicle Compliance Scanning with real-time regulatory verification",
      "Predictive Maintenance using diagnostic sensor data analysis",
      "Autonomous Vehicle systems with advanced perception algorithms",
      "Connected Car platforms with integrated service ecosystems",
      "Auto InsureTech solutions with driving behavior analytics"
    ],
    href: "/industries/autotech"
  },
  {
    name: "EnergyTech",
    fullName: "Energy Technology",
    icon: Zap,
    gradient: "from-yellow-500 via-amber-400 to-orange-500",
    description: "Innovative AI and ML solutions for energy optimization, grid management, and renewable integration.",
    solutions: [
      "Smart Grid Management with real-time demand-response optimization",
      "Renewable Energy Forecasting using advanced weather prediction models",
      "Energy Consumption Analytics for industrial and commercial facilities",
      "Predictive Maintenance for critical energy infrastructure",
      "Carbon Footprint Monitoring and reduction strategies"
    ],
    href: "/industries/energytech"
  },
  {
    name: "GeoTech",
    fullName: "Geospatial Technology",
    icon: Globe,
    gradient: "from-blue-500 to-cyan-500",
    description: "Advanced geospatial AI solutions for location intelligence, remote sensing analytics, and precision mapping applications.",
    solutions: [
      "Satellite Imagery Analysis using deep learning for pattern recognition",
      "Location Intelligence platforms for business and demographic insights",
      "Precision Agriculture systems optimizing resource usage with GIS",
      "Environmental Monitoring with multi-spectral data analytics",
      "Spatial Risk Assessment for insurance and infrastructure planning"
    ],
    href: "/industries/geotech"
  },
  {
    name: "FoodTech",
    fullName: "Food Technology",
    icon: Utensils,
    gradient: "from-green-500 to-teal-500",
    description: "AI-driven solutions for food production, processing, safety monitoring, and supply chain optimization.",
    solutions: [
      "Supply Chain Traceability with blockchain and IoT integration",
      "Nutritional Analysis using computer vision and spectroscopy",
      "Quality Control Automation with multi-sensor inspection systems",
      "Food Safety Monitoring with predictive contamination detection",
      "Personalized Nutrition Recommendations using AI algorithms"
    ],
    href: "/industries/foodtech"
  },
  {
    name: "MarTech",
    fullName: "Marketing Technology",
    icon: ShoppingCart,
    gradient: "from-orange-500 to-pink-600",
    description: "AI solutions for personalized marketing, customer insights, and campaign optimization.",
    solutions: [
      "Customer Segmentation AI for targeted marketing campaigns",
      "Predictive Analytics for customer behavior and conversion forecasting",
      "AI-powered Content Generation for marketing assets",
      "Sentiment Analysis for brand perception monitoring",
      "Recommendation Engines for personalized marketing"
    ],
    href: "/industries/martech"
  },
  {
    name: "AgriTech",
    fullName: "Agricultural Technology",
    icon: Leaf,
    gradient: "from-green-500 to-emerald-600",
    description: "Advanced AI systems for precision farming, crop monitoring, and agricultural resource optimization.",
    solutions: [
      "Crop Monitoring systems using computer vision and satellite imagery",
      "Yield Prediction models based on environmental and historical data",
      "Smart Irrigation Systems with AI-optimized water usage",
      "Pest and Disease Detection using machine learning",
      "Supply Chain Optimization for agricultural distribution"
    ],
    href: "/industries/agritech"
  },
  {
    name: "GovTech",
    fullName: "Government Technology",
    icon: Building2,
    gradient: "from-blue-600 to-indigo-600",
    description: "Digital transformation solutions for government agencies and public services.",
    solutions: [
      "Public Service Automation to streamline citizen interactions",
      "Civic Engagement Platforms with AI-powered analytics",
      "Smart City Solutions for urban planning and management",
      "Fraud Detection systems for public fund protection",
      "Emergency Response Optimization using predictive analytics"
    ],
    href: "/industries/govtech"
  },
  {
    name: "RegTech",
    fullName: "Regulatory Technology",
    icon: FileText,
    gradient: "from-orange-500 to-red-600",
    description: "AI-based compliance monitoring, risk assessment, and regulatory reporting solutions.",
    solutions: [
      "Compliance Monitoring with real-time updates and alerts",
      "Risk Assessment frameworks using predictive modeling",
      "Automated Regulatory Reporting with audit trails",
      "Policy Management systems with AI-assisted updates",
      "KYC/AML Screening using advanced pattern recognition"
    ],
    href: "/industries/regtech"
  },
  {
    name: "FinTech",
    fullName: "Financial Technology",
    icon: BadgeDollarSign,
    gradient: "from-violet-600 via-indigo-500 to-blue-500",
    description: "Innovative AI applications for financial services, trading, and risk management.",
    solutions: [
      "Fraud Detection systems with real-time monitoring",
      "Algorithmic Trading platforms with machine learning optimization",
      "Credit Scoring models based on alternative data",
      "Customer Insights for personalized financial services",
      "Financial Forecasting using time-series analysis"
    ],
    href: "/industries/fintech"
  },
  {
    name: "HealthTech",
    fullName: "Healthcare Technology",
    icon: Stethoscope,
    gradient: "from-cyan-500 via-blue-600 to-purple-600",
    description: "AI-powered healthcare solutions for diagnosis, patient care, and medical research.",
    solutions: [
      "Medical Imaging Analysis with deep learning",
      "Patient Monitoring systems with predictive alerts",
      "Drug Discovery acceleration through AI modeling",
      "Clinical Decision Support using NLP and knowledge graphs",
      "Healthcare Resource Optimization with predictive analytics"
    ],
    href: "/industries/healthtech"
  },
  {
    name: "LegalTech",
    fullName: "Legal Technology",
    icon: Scale,
    gradient: "from-indigo-500 via-purple-500 to-pink-500",
    description: "Advanced AI tools for legal research, document analysis, and case management.",
    solutions: [
      "Contract Analysis using natural language processing",
      "Legal Research Automation with semantic search",
      "Case Prediction models based on historical outcomes",
      "Document Management with intelligent classification",
      "Compliance Verification for legal requirements"
    ],
    href: "/industries/legaltech"
  },
  {
    name: "InsureTech",
    fullName: "Insurance Technology",
    icon: ShieldCheck,
    gradient: "from-fuchsia-500 via-purple-600 to-indigo-500",
    description: "Innovative AI applications for risk assessment, claims processing, and customer service.",
    solutions: [
      "Risk Assessment using multi-factor analysis",
      "Automated Claims Processing with fraud detection",
      "Customer Segmentation for personalized insurance products",
      "Predictive Maintenance for insured assets",
      "Chatbots for policy inquiries and customer support"
    ],
    href: "/industries/insuretech"
  }
];

export default function Industries() {
  const params = useParams();
  const currentLocale = params?.locale as string || 'en';
  
  // Apply localization to all industry data
  const industries = industriesData.map(industry => ({
    ...industry,
    name: localizeSpelling(industry.name, currentLocale),
    fullName: localizeSpelling(industry.fullName, currentLocale),
    description: localizeSpelling(industry.description, currentLocale),
    solutions: industry.solutions.map(solution => 
      localizeSpelling(solution, currentLocale)
    )
  }));

  return (
    <div className="container mx-auto max-w-7xl px-4 py-16 mt-20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="space-y-12"
      >
        <div className="text-center space-y-4 mb-12">
          <h1 className="text-4xl font-bold tracking-tight">
            {localizeSpelling("Industry-Specific AI Solutions", currentLocale)}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {localizeSpelling("Our AI expertise spans across multiple industries, delivering tailored solutions for each sector's unique challenges and opportunities.", currentLocale)}
          </p>
        </div>

        <div className="space-y-20">
          {industries.map((industry, index) => (
            <motion.div
              key={`${industry.name}-${index}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ 
                opacity: 1, 
                y: 0,
                transition: {
                  duration: 0.5,
                  delay: index * 0.1
                }
              }}
              viewport={{ once: true, margin: "-100px" }}
              className={`flex flex-col lg:flex-row gap-8 ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}
            >
              <div className={`flex-1 ${index % 2 !== 0 ? 'lg:pl-10' : 'lg:pr-10'}`}>
                <div className="flex items-center gap-4 mb-4">
                  <div className={`bg-gradient-to-br ${industry.gradient} w-12 h-12 rounded-xl flex items-center justify-center`}>
                    <industry.icon className="text-white h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{industry.name}</h2>
                    <p className="text-muted-foreground">{industry.fullName}</p>
                  </div>
                </div>
                <p className="text-lg mb-6">{industry.description}</p>
                <ul className="space-y-2 mb-6">
                  {industry.solutions.map((solution, i) => (
                    <li key={i} className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>{solution}</span>
                    </li>
                  ))}
                </ul>
                <Link href={industry.href} className="inline-flex items-center font-medium text-primary hover:underline">
                  {localizeSpelling("Learn more about our solutions", currentLocale)}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
              
              <div className="flex-1">
                {/* Modern glassmorphism card matching homepage design exactly */}
                <div className={`rounded-2xl overflow-hidden h-full relative bg-gradient-to-br ${industry.gradient} p-[1px] shadow-lg hover:shadow-xl transition-all duration-500 group`}>
                  
                  {/* Glass morphism background */}
                  <div className="absolute inset-[1px] bg-white/95 backdrop-blur-xl rounded-2xl">
                    {/* Subtle gradient overlay */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${industry.gradient} opacity-5 group-hover:opacity-10 transition-opacity duration-500`}></div>
                    
                    {/* Animated shine effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 group-hover:animate-shimmer opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                  </div>
                  
                  {/* Content */}
                  <div className="relative z-10 p-7 flex flex-col h-full">
                    {/* Icon with gradient background */}
                    <div className={`relative mb-4 w-12 h-12 rounded-xl bg-gradient-to-br ${industry.gradient} p-3 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110`}>
                      <industry.icon 
                        className="w-full h-full text-white drop-shadow-sm" 
                        strokeWidth={1.5}
                      />
                      {/* Icon glow effect */}
                      <div className={`absolute inset-0 rounded-xl bg-gradient-to-br ${industry.gradient} blur-md opacity-50 group-hover:opacity-75 transition-opacity duration-300 -z-10`}></div>
                    </div>
                    
                    {/* Title and description */}
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-slate-800 transition-colors duration-300">
                        {localizeSpelling("Key Applications", currentLocale)}
                      </h3>
                      <p className="text-slate-600 text-sm font-medium leading-relaxed mb-4">
                        {localizeSpelling("Our specialized industry solutions", currentLocale)}
                      </p>
                      
                      {/* Solutions showcase */}
                      <div className="space-y-3 mb-4">
                        {industry.solutions.slice(0, 3).map((solution, i) => {
                          // Extract the main solution name before "with" or "using"
                          const solutionName = solution.split(/\swith\s|\susing\s/)[0];
                          
                          return (
                            <div key={i} className="flex items-start gap-3">
                              <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${industry.gradient} mt-2 flex-shrink-0 shadow-sm`}></div>
                              <span className="text-slate-700 text-sm font-medium leading-relaxed">{solutionName}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Bottom action area */}
                    <div className="flex justify-end mt-4">
                      <Link href={industry.href} className={`w-6 h-6 rounded-full bg-gradient-to-r ${industry.gradient} flex items-center justify-center shadow-md group-hover:shadow-lg transition-all duration-300 group-hover:scale-110`}>
                        <ArrowRight className="w-3 h-3 text-white" strokeWidth={2} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
} 